/**
 * UserManagementAddPage — standalone full-page version of Angular's UserManagementAddComponent.
 * Two-phase flow: Phase 1 (email lookup) → Phase 2 (user details + role assignment).
 * Business logic from USER_MANAGEMENT_MODULE.md §3.
 */
import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, Mail, User, AlertCircle } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { useUserRole } from '@/hooks/useUserRole'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { showSuccess, showError, showValidationMessage, formatApiError } from '@/lib/utils/notifications'
import { trackApplicationEvent } from '@/lib/utils/analytics'
import { checkEmail, createUser } from '../services/userManagementApi'
import { validateName, validateMobile, EMAIL_REGEX } from '../utils/userManagementValidators'
import { formatPhoneInputDisplay, cleanPhone } from '../utils/userManagementFormatters'
import { cn } from '@/lib/utils/helpers'
import { LIBERTY_FOCUS_INPUT } from '../utils/libertyAdminUi'
import { SectionCard } from '@/components/ui/SectionCard'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'

const inputClass = (err?: string) =>
  cn(
    'w-full max-w-full h-9 rounded-md border bg-white px-3 text-sm text-slate-900 shadow-sm',
    'placeholder:text-slate-400 transition-all',
    LIBERTY_FOCUS_INPUT,
    err ? 'border-rose-400' : 'border-slate-200'
  )
const labelClass = 'mb-1.5 block text-xs font-medium text-slate-600'
const sectionHintClass = 'mb-4 text-xs leading-relaxed text-slate-500'
const formCellClass = 'min-w-0'
const emailGridClass =
  'grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
const emailWideClass = 'sm:col-span-2 lg:col-span-2 xl:col-span-2'

export function UserManagementAddPage() {
  const navigate = useNavigate()
  const { isLibertyRole } = useUserRole()

  const [existingUserConfirmOpen, setExistingUserConfirmOpen] = useState(false)
  const [existingUser, setExistingUser] = useState<{
    userId: number
    name: string
    isLibertyUser: boolean
    firstName?: string
    lastName?: string
    phone?: string
    jobTitle?: string
  } | null>(null)

  // Phase 1
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [searching, setSearching] = useState(false)

  // Phase 2 visibility
  const [phase, setPhase] = useState<'identify' | 'details'>('identify')
  const [userId, setUserId] = useState<number | null>(null)
  const [userNotFound, setUserNotFound] = useState(false)
  const [confirmEmail, setConfirmEmail] = useState('')
  const [confirmEmailLocked, setConfirmEmailLocked] = useState(true)
  const [, setConfirmEmailTouched] = useState(false)

  // Phase 2 fields
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [, setLastNameTouched] = useState(false)
  const [phone, setPhone] = useState('')
  const [jobTitle, setJobTitle] = useState('')

  // Role fields (only in standalone mode all roles are available via Assign Role page)
  // In this standalone page we just handle creating the user and navigating to roles
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  // ─── Email lookup ─────────────────────────────────────────────────────────────

  const runEmailLookup = useCallback(async () => {
    const e = email.trim()
    if (!e) { setEmailError('Email is required.'); return }
    if (!EMAIL_REGEX.test(e)) { setEmailError('Email is not valid.'); return }
    setEmailError('')
    setSearching(true)
    try {
      const result = await checkEmail(e)
      if (result) {
        // User exists (Angular shows a confirm modal first)
        if (result.liberty_role && !isLibertyRole) {
          showError('Cannot Add Role To Liberty Users')
          return
        }
        const uid = result.user_id ?? null
        if (uid == null) {
          setUserId(null)
          setUserNotFound(true)
          setPhase('details')
          return
        }
        setExistingUser({
          userId: uid,
          name: `${result.first_name ?? ''} ${result.last_name ?? ''}`.trim() || 'this user',
          isLibertyUser: !!result.liberty_role,
          firstName: result.first_name ?? '',
          lastName: result.last_name ?? '',
          phone: cleanPhone(result.mobile_no ?? ''),
          jobTitle: result.job_title ?? '',
        })
        setExistingUserConfirmOpen(true)
      } else {
        // 404 — new user
        if (e.includes('@libertyrent') && !isLibertyRole) {
          showError('Cannot Add Liberty User')
          return
        }
        setUserId(null)
        setUserNotFound(true)
        setFirstName('')
        setLastName('')
        setPhone('')
        setJobTitle('')
        setConfirmEmail('')
        setConfirmEmailLocked(true)
        setConfirmEmailTouched(false)
        setPhase('details')
      }
    } catch {
      if (e.includes('@libertyrent') && !isLibertyRole) {
        showError('Cannot Add Liberty User')
        return
      }
      setUserId(null)
      setUserNotFound(true)
      setFirstName('')
      setLastName('')
      setPhone('')
      setJobTitle('')
      setConfirmEmail('')
      setConfirmEmailLocked(true)
      setConfirmEmailTouched(false)
      setPhase('details')
    } finally {
      setSearching(false)
    }
  }, [email, isLibertyRole])

  const handleEmailChange = (val: string) => {
    setEmail(val)
    setEmailError('')
    setExistingUserConfirmOpen(false)
    setExistingUser(null)
    if (phase === 'details') {
      // User changed email after lookup — go back to phase 1
      setPhase('identify')
      setFormErrors({})
      setUserId(null)
      setUserNotFound(false)
      setConfirmEmail('')
      setConfirmEmailLocked(true)
      setConfirmEmailTouched(false)
    }
  }

  // ─── Phase 2 validation ──────────────────────────────────────────────────────

  const getConfirmEmailError = useCallback(
    (val: string): string => {
      const v = val.trim()
      if (!v) return 'Confirm Email is required.'
      if (!EMAIL_REGEX.test(v)) return 'Confirm Email is not valid.'
      if (v !== email.trim()) return 'Email and Confirm Email not Matched.'
      return ''
    },
    [email],
  )

  const getLastNameError = useCallback((val: string): string => {
    if (!val) return 'Last Name is required.'
    if (!validateName(val)) return 'Enter Valid Last Name'
    return ''
  }, [])

  const getFirstNameError = useCallback((val: string): string => {
    if (!val) return 'First Name is required.'
    if (!validateName(val)) return 'Enter Valid First Name'
    return ''
  }, [])

  const getPhoneError = useCallback((val: string): string => {
    const v = val.trim()
    if (!v) return 'Contact Phone is required.'
    if (!validateMobile(v)) return 'Enter Valid Contact Phone, Should match pattern [(+X)XXX-XXX-XXXX].'
    return ''
  }, [])

  const validateDetails = (): boolean => {
    const err: Record<string, string> = {}
    if (userNotFound) {
      const ce = getConfirmEmailError(confirmEmail)
      if (ce) err.confirm_email = ce
    }
    const fn = getFirstNameError(firstName)
    if (fn) err.first_name = fn
    const ln = getLastNameError(lastName)
    if (ln) err.last_name = ln
    const ph = getPhoneError(phone)
    if (ph) err.phone = ph
    setFormErrors(err)
    return Object.keys(err).length === 0
  }

  // ─── Submit ───────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateDetails()) {
      setTimeout(() => {
        const firstErrEl = document.querySelector<HTMLElement>(
          'input.border-rose-500, [data-testid^="um-add-"].border-rose-500'
        )
        if (firstErrEl) {
          firstErrEl.focus()
          firstErrEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 0)
      return
    }
    setSubmitting(true)
    try {
      let targetUserId = userId

      if (targetUserId == null) {
        const newUserId = await createUser({
          email: email.trim(),
          confirm_email: userNotFound ? confirmEmail.trim() : undefined,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          mobile_no: cleanPhone(phone),
          job_title: jobTitle.trim() || undefined,
        })
        targetUserId = newUserId
      }

      showSuccess('User Added Successfully!')
      trackApplicationEvent('user_added', 'user_management')
      // Navigate to edit page to assign role
      navigate(`/user-management/edit/${targetUserId}`)
    } catch (err) {
      showValidationMessage(formatApiError(err), 'Validation Message')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] font-sans text-slate-900 pb-12" data-testid="um-add-page">
      <PageHeader title="USER MANAGEMENT ADD" />

      <div className="w-full px-3 sm:px-6 py-4 sm:py-6">
        <SectionCard>

          <form
            onSubmit={phase === 'identify' ? (e) => { e.preventDefault(); void runEmailLookup() } : handleSubmit}
            className="px-4 sm:px-6 py-6 space-y-8"
            autoComplete="off"
            noValidate
            data-testid="um-add-form"
          >
            {/* ── Email / account lookup ─────────────────────────────────────── */}
            <section aria-labelledby="um-add-section-email">
              <div id="um-add-section-email" className="mb-4 flex items-center gap-3 border-b border-slate-100 pb-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#dbeafe] border border-[#c7ddff] text-[#2563eb] shadow-sm">
                  <Mail className="h-4 w-4" aria-hidden />
                </span>
                <span className="text-base font-bold text-slate-800">Email Address</span>
              </div>
              {phase === 'details' && !userNotFound && (
                <p className={sectionHintClass}>
                  This email is linked to an existing user. Review or update their details below.
                </p>
              )}
              <div className="rounded-xl border border-slate-200/90 bg-gradient-to-b from-slate-50/90 to-white p-5 shadow-sm ring-1 ring-slate-100/60 sm:p-6">
                <div className={emailGridClass}>
                  <div className={cn(formCellClass, emailWideClass)}>
                    <label htmlFor="um-add-email" className={labelClass}>
                      Email <span className="text-rose-500">*</span>
                    </label>
                    <input
                      id="um-add-email"
                      type="email"
                      autoComplete="email"
                      placeholder="name@company.com"
                      value={email}
                      onChange={(e) => handleEmailChange(e.target.value)}
                      className={inputClass(emailError)}
                      data-testid="um-add-email-input"
                    />
                    {emailError && (
                      <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-rose-600" data-testid="um-add-email-error">
                        <AlertCircle className="h-4 w-4 shrink-0 text-rose-600" aria-hidden />
                        <span>{emailError}</span>
                      </div>
                    )}
                  </div>

                  {phase === 'details' && userNotFound && (
                    <div className={cn(formCellClass, emailWideClass)}>
                      <label htmlFor="um-add-confirm-email" className={labelClass}>
                        Confirm Email <span className="text-rose-500">*</span>
                      </label>
                      <input
                        id="um-add-confirm-email"
                        type="text"
                        inputMode="email"
                        name="um_add_confirm_email"
                        placeholder="Confirm Email"
                        autoComplete="off"
                        readOnly={confirmEmailLocked}
                        value={confirmEmail}
                        onFocus={() => {
                          if (confirmEmailLocked) {
                            setConfirmEmailLocked(false)
                            setConfirmEmail('')
                            setConfirmEmailTouched(false)
                          }
                        }}
                        onChange={(e) => {
                          const next = e.target.value
                          if (confirmEmailLocked) {
                            setConfirmEmailLocked(false)
                          }
                          setConfirmEmail(next)
                          // Blur-only validation (QA): clear any existing error while typing.
                          setFormErrors((p) => ({ ...p, confirm_email: '' }))
                        }}
                        onBlur={() => {
                          setConfirmEmailTouched(true)
                          const msg = getConfirmEmailError(confirmEmail)
                          setFormErrors((p) => ({ ...p, confirm_email: msg }))
                        }}
                        className={inputClass(formErrors.confirm_email)}
                        data-testid="um-add-confirm-email-input"
                      />
                      {formErrors.confirm_email && (
                        <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-rose-600" data-testid="um-add-confirm-email-error">
                          <AlertCircle className="h-4 w-4 shrink-0 text-rose-600" aria-hidden />
                          <span>{formErrors.confirm_email}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* ── Personal Information (phase 2) ──────────────────────────────── */}
            {phase === 'details' && (
              <section aria-labelledby="um-add-section-personal">
                <div id="um-add-section-personal" className="mb-4 flex items-center gap-3 border-b border-slate-100 pb-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#dbeafe] border border-[#c7ddff] text-[#2563eb] shadow-sm">
                    <User className="h-4 w-4" aria-hidden />
                  </span>
                  <span className="text-base font-bold text-slate-800">Personal Information</span>
                </div>

                <div className="rounded-xl border border-slate-200/90 bg-gradient-to-b from-slate-50/90 to-white p-5 shadow-sm ring-1 ring-slate-100/60 sm:p-6">
                  <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2">
                    <div className={formCellClass}>
                      <label htmlFor="um-add-first-name" className={labelClass}>
                        First Name <span className="text-rose-500">*</span>
                      </label>
                      <input
                        id="um-add-first-name"
                        type="text"
                        autoComplete="given-name"
                        placeholder="First Name"
                        value={firstName}
                        onChange={(e) => {
                          setFirstName(e.target.value)
                          // Blur-only validation (QA): clear any existing error while typing.
                          setFormErrors((p) => ({ ...p, first_name: '' }))
                        }}
                        onBlur={() => {
                          const msg = getFirstNameError(firstName)
                          setFormErrors((p) => ({ ...p, first_name: msg }))
                        }}
                        className={inputClass(formErrors.first_name)}
                        data-testid="um-add-first-name-input"
                      />
                      {formErrors.first_name && (
                        <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-rose-600" data-testid="um-add-first-name-error">
                          <AlertCircle className="h-4 w-4 shrink-0 text-rose-600" aria-hidden />
                          <span>{formErrors.first_name}</span>
                        </div>
                      )}
                    </div>

                    <div className={formCellClass}>
                      <label htmlFor="um-add-last-name" className={labelClass}>
                        Last Name <span className="text-rose-500">*</span>
                      </label>
                      <input
                        id="um-add-last-name"
                        type="text"
                        autoComplete="family-name"
                        placeholder="Last Name"
                        value={lastName}
                        onChange={(e) => {
                          const next = e.target.value
                          setLastName(next)
                          // Blur-only validation (QA): clear any existing error while typing.
                          setFormErrors((p) => ({ ...p, last_name: '' }))
                        }}
                        onBlur={() => {
                          setLastNameTouched(true)
                          const msg = getLastNameError(lastName)
                          setFormErrors((p) => ({ ...p, last_name: msg }))
                        }}
                        className={inputClass(formErrors.last_name)}
                        data-testid="um-add-last-name-input"
                      />
                      {formErrors.last_name && (
                        <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-rose-600" data-testid="um-add-last-name-error">
                          <AlertCircle className="h-4 w-4 shrink-0 text-rose-600" aria-hidden />
                          <span>{formErrors.last_name}</span>
                        </div>
                      )}
                    </div>

                    <div className={formCellClass}>
                      <label htmlFor="um-add-phone" className={labelClass}>
                        Phone <span className="text-rose-500">*</span>
                      </label>
                      <input
                        id="um-add-phone"
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        placeholder="Phone"
                        value={formatPhoneInputDisplay(phone)}
                        onChange={(e) => {
                          setPhone(e.target.value.replace(/\D/g, ''))
                          // Blur-only validation (QA): clear any existing error while typing.
                          setFormErrors((p) => ({ ...p, phone: '' }))
                        }}
                        onBlur={() => {
                          const msg = getPhoneError(phone)
                          setFormErrors((p) => ({ ...p, phone: msg }))
                        }}
                        className={inputClass(formErrors.phone)}
                        data-testid="um-add-phone-input"
                      />
                      {formErrors.phone && (
                        <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-rose-600" data-testid="um-add-phone-error">
                          <AlertCircle className="h-4 w-4 shrink-0 text-rose-600" aria-hidden />
                          <span>{formErrors.phone}</span>
                        </div>
                      )}
                    </div>

                    <div className={formCellClass}>
                      <label htmlFor="um-add-job-title" className={labelClass}>
                        Job Title <span className="font-normal text-slate-400">(optional)</span>
                      </label>
                      <input
                        id="um-add-job-title"
                        type="text"
                        autoComplete="organization-title"
                        placeholder="Job Title"
                        value={jobTitle}
                        onChange={(e) => setJobTitle(e.target.value)}
                        className={inputClass()}
                        data-testid="um-add-job-title-input"
                      />
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 pt-5">
              <button
                type="submit"
                disabled={submitting || searching}
                className={cn(primaryEntityActionButtonClass, 'px-6')}
                data-testid={phase === 'identify' ? 'um-add-search-btn' : userId ? 'um-add-go-to-edit-btn' : 'um-add-submit-btn'}
              >
                {(submitting || searching) && <Loader2 className="h-4 w-4 shrink-0 animate-spin" />}
                {phase === 'identify' ? 'Search' : userId ? 'Go to Edit' : 'Add User'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/user-management')}
                className={secondaryEntityActionButtonClass}
                data-testid="um-add-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </form>
        </SectionCard>
      </div>

      <ConfirmDialog
        isOpen={existingUserConfirmOpen && existingUser != null}
        title="Liberty Account Exists!"
        message={`Liberty Account already exists for this email and is registered to: ${existingUser?.name ?? ''}\n\nWould you like to edit this user?`}
        primaryLabel="Yes"
        onPrimary={() => {
          if (existingUser?.userId != null) navigate(`/user-management/edit/${existingUser.userId}`)
        }}
        secondaryLabel="No"
        onSecondary={() => {
          setExistingUserConfirmOpen(false)
          setExistingUser(null)
          setEmail('')
          setEmailError('')
          setUserId(null)
          setUserNotFound(false)
          setFirstName('')
          setLastName('')
          setPhone('')
          setJobTitle('')
          setConfirmEmail('')
          setConfirmEmailLocked(true)
          setConfirmEmailTouched(false)
          setFormErrors({})
          setPhase('identify')
        }}
        onDismiss={() => setExistingUserConfirmOpen(false)}
        primaryFirst
        overlayDataTestId="um-add-account-exists-overlay"
        primaryButtonDataTestId="um-add-account-exists-yes-btn"
        secondaryButtonDataTestId="um-add-account-exists-no-btn"
        closeButtonDataTestId="um-add-account-exists-close-btn"
      />
    </div>
  )
}
